/**
 * Display formatting for the status bar and the settings panel.
 *
 * Everything here is lowercase and abbreviated on purpose: these strings sit in
 * 12px monospace chrome where a full sentence would shout.
 */

import { MAX_TIME } from '@shared/sync-state'

const MINUTE = 60_000
const HOUR = 60 * MINUTE
const DAY = 24 * HOUR

/** Hairline gap between shortcut keys — narrower than a word space, no "+". */
const THIN_SPACE = '\u2009'

const SAME_YEAR_DATE = new Intl.DateTimeFormat(undefined, { day: 'numeric', month: 'short' })
const OTHER_YEAR_DATE = new Intl.DateTimeFormat(undefined, {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
})

function startOfDay(timestamp: number): number {
  const date = new Date(timestamp)
  date.setHours(0, 0, 0, 0)
  return date.getTime()
}

/**
 * `now` is injectable so a caller can re-render a whole list against a single
 * clock reading instead of letting adjacent rows disagree about the time.
 *
 * Not every timestamp reaching here was stamped by Jot: `updatedAt` on the wire
 * comes from a Jot Server, and on desktop it is read from a note file's header
 * that any other tool may have written. Validating at the edges is the real fix
 * and is done there; refusing to be the line that throws is the guarantee this
 * function can make on its own, because a formatter that takes down the tree is
 * out of all proportion to a date it cannot render.
 */
export function relativeTime(timestamp: number, now: number = Date.now()): string {
  // Both guards go first, and both are needed: outside this window
  // `Intl.DateTimeFormat.format` throws `RangeError`, and `startOfDay` and
  // `getFullYear` produce `NaN`, which throws in the very same place one line
  // later. `unknown` rather than a plausible-looking date: a fabricated time in a
  // Conflict summary is what ADR-0002 says the user decides on.
  if (!Number.isFinite(timestamp) || Math.abs(timestamp) > MAX_TIME) return 'unknown'
  if (!Number.isFinite(now) || Math.abs(now) > MAX_TIME) return 'unknown'

  const diff = now - timestamp

  // A note saved a moment ago can carry a timestamp slightly in the future when
  // the storage backend stamps it; never render a negative age.
  if (diff < 45_000) return 'just now'
  if (diff < HOUR) return `${Math.round(diff / MINUTE)} min ago`

  const days = Math.round((startOfDay(now) - startOfDay(timestamp)) / DAY)
  if (days === 0) return `${Math.round(diff / HOUR)} h ago`
  if (days === 1) return 'yesterday'

  const sameYear = new Date(timestamp).getFullYear() === new Date(now).getFullYear()
  const formatter = sameYear ? SAME_YEAR_DATE : OTHER_YEAR_DATE
  return formatter.format(timestamp).toLowerCase()
}

/**
 * A byte count for the status row.
 *
 * KiB below a megabyte, because "0.0 MiB" is not a size, and one decimal above it:
 * the sizes this renders are a scratchpad the user has to shorten and a cap they may
 * have to raise, so the two have to be comparable at a glance.
 */
export function byteSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return 'unknown'
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KiB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MiB`
}

function modifierLabel(token: string, mac: boolean, windows: boolean): string | null {
  switch (token) {
    case 'commandorcontrol':
    case 'cmdorctrl':
      return mac ? 'Cmd' : 'Ctrl'
    case 'command':
    case 'cmd':
    case 'meta':
    case 'super':
      return mac ? 'Cmd' : windows ? 'Win' : 'Super'
    case 'control':
    case 'ctrl':
      return 'Ctrl'
    case 'alt':
    case 'option':
      return mac ? 'Opt' : 'Alt'
    case 'altgr':
      return 'AltGr'
    case 'shift':
      return 'Shift'
    default:
      return null
  }
}

const KEY_LABELS: Record<string, string> = {
  plus: '+',
  space: 'Space',
  tab: 'Tab',
  backspace: 'Backspace',
  delete: 'Del',
  escape: 'Esc',
  esc: 'Esc',
  return: 'Enter',
  enter: 'Enter',
  up: 'Up',
  down: 'Down',
  left: 'Left',
  right: 'Right',
  pageup: 'PgUp',
  pagedown: 'PgDn',
}

/**
 * Render an Electron accelerator as human keys, e.g.
 * `CommandOrControl+Shift+Space` -> `Cmd Shift Space`.
 *
 * Symbols (⌘, ⇧) are deliberately avoided: the UI is monospace and glyph
 * coverage across the three platforms is not worth the risk of a tofu box.
 */
export function formatShortcut(accelerator: string, platform: string): string {
  const mac = platform === 'darwin'
  const windows = platform === 'win32'

  return accelerator
    .split('+')
    .map((part) => part.trim())
    .filter((part) => part.length > 0)
    .map((part) => {
      const token = part.toLowerCase()
      const modifier = modifierLabel(token, mac, windows)
      if (modifier) return modifier

      const known = KEY_LABELS[token]
      if (known) return known

      // Single characters and function keys read best fully uppercased.
      if (part.length <= 3) return part.toUpperCase()
      return part.charAt(0).toUpperCase() + part.slice(1)
    })
    .join(THIN_SPACE)
}
